import { useMemo, useState } from 'react'

import { DocumentNode } from '@apollo/client'
import { addMonths } from 'date-fns'
import {
  DatePickerValue,
  F0Box,
  F0Card,
  F0Checkbox,
  F0DatePicker,
  F0Dialog,
  F0Select,
  F0Text,
} from '@factorialco/f0-react'
import { Input, NumberInput } from '@factorialco/f0-react/dist/experimental'
import { ChartLine, Target } from '@factorialco/f0-react/icons/app'
import {
  GoalsCreateMetricGroupDocument,
  GoalsCreateMetricGroupMutationVariables,
  GoalsCreateMetricGroupResultFragment,
  PerformanceCyclesCycleSelectFieldDocument,
} from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

type MeasurementType = 'qualitative' | 'quantitative'

type FormValues = {
  title: string
  cycleId: string | undefined
  assigneeId: number | undefined
  isQuantitative: boolean
  isPublic: boolean
  startValue: number
  targetValue: number
}

type GoalCreateDialogProps = {
  onClose: () => void
  onSuccess?: (metricGroup: GoalsCreateMetricGroupResultFragment) => void
  refetchQueries?: DocumentNode[]
}

const toDatePickerValue = (date: Date): DatePickerValue => ({
  granularity: 'day' as const,
  value: { from: date, to: date },
})

const formatDateForBackend = (value: DatePickerValue | undefined): string =>
  value?.value?.from != null
    ? i18nDateToString(value.value.from, 'backend')
    : i18nDateToString(new Date(), 'backend')

export const GoalCreateDialog: React.FC<GoalCreateDialogProps> = ({
  onClose,
  onSuccess,
  refetchQueries = [],
}) => {
  const [startDateValue, setStartDateValue] = useState<DatePickerValue | undefined>(() =>
    toDatePickerValue(new Date())
  )
  const [endDateValue, setEndDateValue] = useState<DatePickerValue | undefined>(() =>
    toDatePickerValue(addMonths(new Date(), 3))
  )

  const { data: cyclesData } = useQuery(PerformanceCyclesCycleSelectFieldDocument)
  const cycleOptions = useMemo(() => {
    const cycles = orderBy(
      cyclesData?.performanceCycles.cycles ?? [],
      ['startOn', 'title'],
      ['desc', 'asc']
    )
    return cycles.map((cycle) => ({
      value: cycle.id.toString(),
      label: cycle.title,
    }))
  }, [cyclesData])

  const [createGoalWithMetric] = useMutation(GoalsCreateMetricGroupDocument, {
    extractorFn: (data) => data.goalsMutations.createGoalsMetricGroup,
    refetchQueries,
    awaitRefetchQueries: true,
  })

  const form = useForm<FormValues>({
    defaultValues: {
      title: '',
      cycleId: undefined,
      assigneeId: undefined,
      isQuantitative: false,
      isPublic: false,
      startValue: 0,
      targetValue: 100,
    },
    validate(values) {
      const errors: Partial<Record<keyof FormValues, string>> = {}

      if (!values.title.trim()) {
        errors.title = i18n.t('goals_revamp.create_dialog.validation.title_required')
      }

      if (!values.assigneeId) {
        errors.assigneeId = i18n.t('goals_revamp.create_dialog.validation.assignee_required')
      }

      return errors
    },
    onSubmit: async (values) => {
      const metricStartOn = formatDateForBackend(startDateValue)
      const metricEndOn = formatDateForBackend(endDateValue)

      if (!values.assigneeId) {
        throw new Error('assigneeId is required')
      }

      const variables: GoalsCreateMetricGroupMutationVariables = {
        title: values.title,
        cycleIds: values.cycleId ? [values.cycleId] : undefined,
        public: values.isPublic,
        metricConfiguration: {
          title: values.title,
          startOn: metricStartOn,
          endOn: metricEndOn,
          assigneeId: values.assigneeId,
          quantitativeValue: values.isQuantitative
            ? {
                startValue: values.startValue,
                targetValue: values.targetValue,
                currentValue: values.startValue,
              }
            : undefined,
        },
      }

      const result = await createGoalWithMetric({ variables })
      const handled = handleResult(result, (formErrors) => {
        form.setErrors(formErrors)
      })

      if (handled.success) {
        const metricGroup = handled.data.metricGroup
        if (!metricGroup) throw new Error('No metric group returned')

        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        track(trackingEvents.goals.createGoal, {
          has_cycle: values.cycleId != null,
          has_quantitative_kr: values.isQuantitative,
        })
        onSuccess?.(metricGroup)
      }

      return handled
    },
  })

  const handleMeasurementTypeChange = (type: MeasurementType) => {
    form.setFieldValue('isQuantitative', type === 'quantitative')
  }

  const handleAssigneeChange = (id: number | undefined) => {
    form.setFieldValue('assigneeId', id)
  }

  const handleStartDateChange = (
    value: DatePickerValue | undefined,
    _stringValue?: string | undefined
  ) => {
    setStartDateValue(value)
  }

  const handleEndDateChange = (
    value: DatePickerValue | undefined,
    _stringValue?: string | undefined
  ) => {
    setEndDateValue(value)
  }

  return (
    <F0Dialog
      isOpen
      width="md"
      onClose={() => {
        if (form.isSubmitting) return
        onClose()
      }}
      title={i18n.t('goals_revamp.create_dialog.title')}
      primaryAction={{
        label: i18n.t('goals_revamp.create_dialog.actions.create'),
        onClick: () => form.handleSubmit(),
        disabled: form.isSubmitting || !form.isValid,
        loading: form.isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('goals_revamp.create_dialog.actions.cancel'),
        onClick: () => {
          if (form.isSubmitting) return
          onClose()
        },
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="lg">
        <Input
          error={form.touched.title ? form.errors.title?.toString() : undefined}
          label={i18n.t('goals_revamp.create_dialog.fields.goal_name')}
          placeholder={i18n.t('goals_revamp.create_dialog.fields.goal_name_placeholder')}
          value={form.values.title}
          onChange={(value: string) => form.setFieldValue('title', value)}
          size="md"
        />

        <EmployeeSelectorV2
          label={i18n.t('goals_revamp.create_dialog.fields.assigned_to')}
          value={form.values.assigneeId}
          onChange={handleAssigneeChange}
          mandatory
          error={form.touched.assigneeId ? form.errors.assigneeId?.toString() : undefined}
        />

        <F0Select
          label={i18n.t('goals_revamp.create_dialog.fields.cycle')}
          placeholder={i18n.t('goals_revamp.create_dialog.fields.cycle_placeholder')}
          options={cycleOptions}
          value={form.values.cycleId}
          onChange={(value: string) => form.setFieldValue('cycleId', value || undefined)}
          size="md"
        />

        <F0Box display="flex" flexDirection="row" gap="md">
          <F0Box grow>
            <F0DatePicker
              label={i18n.t('goals_revamp.create_dialog.fields.start_date')}
              value={startDateValue}
              onChange={handleStartDateChange}
              maxDate={endDateValue?.value?.from}
              size="md"
            />
          </F0Box>
          <F0Box grow>
            <F0DatePicker
              label={i18n.t('goals_revamp.create_dialog.fields.end_date')}
              value={endDateValue}
              onChange={handleEndDateChange}
              minDate={startDateValue?.value?.from}
              size="md"
            />
          </F0Box>
        </F0Box>

        <F0Text
          variant="label"
          content={i18n.t('goals_revamp.create_dialog.fields.measurement_type')}
        />

        <F0Box display="flex" flexDirection="row" gap="md">
          <F0Box grow>
            <F0Card
              compact
              selectable
              selected={!form.values.isQuantitative}
              onSelect={() => handleMeasurementTypeChange('qualitative')}
              avatar={{ type: 'icon' as const, icon: Target }}
              title={i18n.t('goals_revamp.create_dialog.measurement.qualitative.title')}
            />
          </F0Box>
          <F0Box grow>
            <F0Card
              compact
              selectable
              selected={form.values.isQuantitative}
              onSelect={() => handleMeasurementTypeChange('quantitative')}
              avatar={{ type: 'icon' as const, icon: ChartLine }}
              title={i18n.t('goals_revamp.create_dialog.measurement.quantitative.title')}
            />
          </F0Box>
        </F0Box>

        {form.values.isQuantitative && (
          <F0Box display="flex" flexDirection="row" gap="md">
            <F0Box grow>
              <NumberInput
                label={i18n.t('goals_revamp.create_dialog.fields.start_value')}
                locale={i18n.locale}
                value={form.values.startValue}
                step={1}
                maxDecimals={2}
                onChange={(value) => form.setFieldValue('startValue', value ?? 0)}
                size="md"
              />
            </F0Box>
            <F0Box grow>
              <NumberInput
                label={i18n.t('goals_revamp.create_dialog.fields.target_value')}
                locale={i18n.locale}
                value={form.values.targetValue}
                step={1}
                maxDecimals={2}
                onChange={(value) => form.setFieldValue('targetValue', value ?? 0)}
                size="md"
              />
            </F0Box>
          </F0Box>
        )}

        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Checkbox
            title={i18n.t('goals_revamp.create_dialog.fields.mark_public')}
            checked={form.values.isPublic}
            onCheckedChange={(checked) => form.setFieldValue('isPublic', checked === true)}
          />
          <F0Text
            variant="description"
            content={i18n.t('goals_revamp.create_dialog.fields.mark_public_description')}
          />
        </F0Box>
      </F0Box>
    </F0Dialog>
  )
}
